import { create } from 'zustand';

export interface HistoryAction {
  id: string;
  type: string;
  description: string;
  timestamp: Date;
  undo: () => Promise<void>;
  redo: () => Promise<void>;
}

interface ActionHistoryState {
  past: HistoryAction[];
  future: HistoryAction[];
  isUndoing: boolean;
  isRedoing: boolean;

  // Actions
  push: (action: Omit<HistoryAction, 'id' | 'timestamp'>) => void;
  undo: () => Promise<void>;
  redo: () => Promise<void>;
  clear: () => void;

  // Getters
  canUndo: () => boolean;
  canRedo: () => boolean;
  getLastAction: () => HistoryAction | undefined;
}

const generateActionId = () =>
  `action-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

export const useActionHistoryStore = create<ActionHistoryState>((set, get) => ({
  past: [],
  future: [],
  isUndoing: false,
  isRedoing: false,

  push: (action) => {
    const newAction: HistoryAction = {
      ...action,
      id: generateActionId(),
      timestamp: new Date(),
    };

    set((state) => ({
      past: [...state.past.slice(-19), newAction], // Garder 20 actions max
      future: [], // Clear redo stack on new action
    }));
  },

  undo: async () => {
    const { past, future, isUndoing } = get();
    if (past.length === 0 || isUndoing) return;

    const action = past[past.length - 1];
    if (!action) return;

    set({ isUndoing: true });

    try {
      await action.undo();

      set({
        past: past.slice(0, -1),
        future: [action, ...future],
        isUndoing: false,
      });
    } catch (error) {
      console.error('Undo failed:', error);
      set({ isUndoing: false });
      throw error;
    }
  },

  redo: async () => {
    const { past, future, isRedoing } = get();
    if (future.length === 0 || isRedoing) return;

    const action = future[0];
    if (!action) return;

    set({ isRedoing: true });

    try {
      await action.redo();

      set({
        past: [...past, action],
        future: future.slice(1),
        isRedoing: false,
      });
    } catch (error) {
      console.error('Redo failed:', error);
      set({ isRedoing: false });
      throw error;
    }
  },

  clear: () => set({ past: [], future: [] }),

  canUndo: () => {
    const { past, isUndoing } = get();
    return past.length > 0 && !isUndoing;
  },

  canRedo: () => {
    const { future, isRedoing } = get();
    return future.length > 0 && !isRedoing;
  },

  getLastAction: () => {
    const { past } = get();
    return past[past.length - 1];
  },
}));

// Convenience hook for action history
export const useActionHistory = () => {
  const {
    push,
    undo,
    redo,
    clear,
    canUndo,
    canRedo,
    getLastAction,
    isUndoing,
    isRedoing,
  } = useActionHistoryStore();

  return {
    push,
    undo,
    redo,
    clear,
    canUndo: canUndo(),
    canRedo: canRedo(),
    lastAction: getLastAction(),
    isUndoing,
    isRedoing,
  };
};
